/* eslint-disable react/no-danger */
// Modules:
import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { Link } from 'react-router-dom';

// Utils:
import { useErrorHandler } from 'react-error-boundary';
import { createMarkup, handleFetchError } from 'utilities/utils';

// Config:
import config from 'config/config';

// Styles:
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Grid from '@mui/material/Grid';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import Skeleton from '@mui/material/Skeleton';
import muiTheme from '../../theme';

const styles = {
    '&.MuiGrid-root': {
        justifyContent: 'center',
        marginTop: '0.5em',
        flexWrap: 'wrap',
    },
    '&.MuiGrid-item': {
        width: '65%',
        maxWidth: 400,
    },
    '&.MuiListItem-root': {
        textDecoration: 'none',
        color: muiTheme.palette.primary.main,
    },
    '&.MuiListItemText-root': {
        paddingLeft: '1.5em',
    },
    '&.MuiListSubheader-root': {
        fontWeight: 'bold',
        color: 'white',
        backgroundColor: muiTheme.palette.navbarBlue.main,
        letterSpacing: '0.03rem',
        marginBottom: '1rem',
        maxWidth: 340,
    },
};

const HomeContent = ({ homeContentTopics }) => {
    const [open, setOpen] = useState(false);
    const [homeContent, setHomeContent] = useState([]);

    // Google Analytics update
    ReactGA.send({ hitType: 'pageview', page: '/', title: 'home' });

    // // Error Hook // //
    const handleError = useErrorHandler();

    // // Effects // //
    // Fetch editable content for About and Resources sections
    useEffect(() => {
        const getHomepageContent = () => {
            const apiContentEndpoint = `${config.homeContentEndpoint}`;

            fetch(apiContentEndpoint)
                .then(handleFetchError)
                .then((res) => res.json())
                .then((json) => setHomeContent(json), handleError)
                .catch(handleError);
        };
        getHomepageContent();
    }, [handleError]);

    // // Methods // //
    const handleExpandClick = () => {
        setOpen(!open);
    };

    // Get the main topic buttons and associated nested topics
    const getTopicButtons = (topicsArray) => {
        const topicsList = topicsArray.map((topic) => {
            // If the topic has nested subtopics
            if (topic.subtopics) {
                return (
                    <button
                        type="button"
                        key={topic.topicTitle}
                        onClick={handleExpandClick}
                        className="home-link-button"
                    >
                        <ListItem button key={topic.topicTitle}>
                            <img
                                className="topic-logo"
                                src={`/ephtportal/images/${topic.topicUrlPath}.svg`}
                                alt="Topic logo"
                            />
                            <ListItemText primary={topic.topicTitle} />
                            {open ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={open} timeout="auto">
                            <List component="div" dense>
                                {getSubTopicButtons(topic.subtopics)}
                            </List>
                        </Collapse>
                    </button>
                );
            }

            // If the topic does not have nested subtopics
            return (
                <Link
                    to={{ pathname: `/${topic.topicUrlPath}` }}
                    className="home-link"
                    key={topic.topicTitle}
                >
                    <ListItem button path={topic.topicUrlPath}>
                        <img
                            className="topic-logo"
                            src={`/ephtportal/images/${topic.topicUrlPath}.svg`}
                            alt="Topic logo"
                        />
                        <ListItemText primary={topic.topicTitle} />
                    </ListItem>
                </Link>
            );
        });

        // Return list sorted by key name in alpha order
        return topicsList.sort((a, b) => (a.key > b.key ? 1 : -1));
    };

    // Get the subtopic buttons for a parent topic
    const getSubTopicButtons = (subTopicsArray) => {
        const subTopicList = subTopicsArray.map((topic) => (
            <Link
                to={{ pathname: `/${topic.topicUrlPath}` }}
                className="home-link"
                key={topic.topicTitle}
            >
                <ListItem button path={topic.topicUrlPath}>
                    <ListItemText primary={topic.topicTitle} sx={styles} />
                </ListItem>
            </Link>
        ));

        // Return list sorted by key name in alpha order
        return subTopicList.sort((a, b) => (a.key > b.key ? 1 : -1));
    };

    // For the home page content there's only a single object returned, so we access it as [0]
    const introText = homeContent[0]?.content;
    return (
        <Grid container direction="row" className="home-container" sx={styles}>
            <Grid item xs={12} md={4} lg={2} className="home-intro-logo">
                {introText ? (
                    <img
                        src={`/ephtportal/images/EPHT_logo.png`}
                        alt="Topic logo"
                        height="200px"
                    />
                ) : (
                    <Skeleton
                        variant="circular"
                        height={200}
                        width={200}
                        animation="wave"
                        className="home-content-image-skeleton"
                    />
                )}
            </Grid>

            <Grid
                container
                direction="row"
                className="home-intro-container"
                columnSpacing={{ md: 1 }}
            >
                <Grid item xs={12} className="home-intro">
                    {introText ? (
                        <div
                            dangerouslySetInnerHTML={createMarkup(introText)}
                        />
                    ) : (
                        <>
                            <Skeleton
                                className="home-content-intro-text-skeleton"
                                animation="wave"
                            />
                            <Skeleton
                                variant="rectangular"
                                height={300}
                                animation="wave"
                            />
                        </>
                    )}
                </Grid>

                <Grid container className="link-container">
                    <Grid item xs={12} sm={6} sx={styles}>
                        <List
                            component="nav"
                            aria-labelledby="nested-list-health-subheader"
                            dense
                            subheader={
                                introText ? (
                                    <ListSubheader
                                        component="div"
                                        id="nested-list-health-subheader"
                                        sx={styles}
                                    >
                                        HEALTH TOPICS
                                    </ListSubheader>
                                ) : (
                                    <Skeleton
                                        variant="rectangular"
                                        height={50}
                                    />
                                )
                            }
                            sx={styles}
                        >
                            {introText
                                ? getTopicButtons(
                                      homeContentTopics.filter(
                                          (topic) => topic.category === 'health'
                                      )
                                  )
                                : Array.from({ length: 5 }, (_, index) => ({
                                      value: `Health item ${index + 1}`,
                                  })).map(({ value }) => (
                                      <Skeleton
                                          key={value}
                                          className="home-content-env-topic-skeleton"
                                          animation="wave"
                                      />
                                  ))}
                        </List>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={styles}>
                        <List
                            component="nav"
                            aria-labelledby="nested-list-env-subheader"
                            dense
                            subheader={
                                introText ? (
                                    <ListSubheader
                                        component="div"
                                        id="nested-list-env-subheader"
                                        sx={styles}
                                    >
                                        ENVIRONMENTAL TOPICS
                                    </ListSubheader>
                                ) : (
                                    <Skeleton
                                        variant="rectangular"
                                        height={50}
                                        className="home-content-env-topic-header-skeleton"
                                    />
                                )
                            }
                            sx={styles}
                        >
                            {introText
                                ? getTopicButtons(
                                      homeContentTopics.filter(
                                          (topic) =>
                                              topic.category === 'environmental'
                                      )
                                  )
                                : Array.from({ length: 3 }, (_, index) => ({
                                      value: `Env item ${index + 1}`,
                                  })).map(({ value }) => (
                                      <Skeleton
                                          key={value}
                                          className="home-content-env-topic-skeleton"
                                          animation="wave"
                                      />
                                  ))}
                        </List>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default HomeContent;
